<template>
	<div class="w-full">
		<!-- Header -->
		<div class="mb-6">
			<h1
				class="text-2xl lg:text-3xl font-bold text-text-primary mb-2 tracking-tight"
			>
				My Insurance Policies
			</h1>
			<p class="text-sm lg:text-base text-text-secondary">
				Manage and track your active insurance policies
			</p>
		</div>

		<!-- Stats Overview -->
		<div class="grid grid-cols-2 gap-4 mb-6">
			<div
				class="bg-white border border-border rounded-lg p-4 hover:shadow-md transition-all duration-300"
			>
				<div class="flex items-center justify-between">
					<div>
						<p class="text-xs lg:text-sm text-text-secondary mb-1">
							Active Policies
						</p>
						<p class="text-xl lg:text-2xl font-bold text-green-600">
							{{ activePoliciesCount }}
						</p>
					</div>
					<div
						class="w-10 h-10 lg:w-12 lg:h-12 bg-green-50 rounded-lg flex items-center justify-center"
					>
						<svg
							class="w-5 h-5 lg:w-6 lg:h-6 text-green-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
					</div>
				</div>
			</div>

			<div
				class="bg-white border border-border rounded-lg p-4 hover:shadow-md transition-all duration-300"
			>
				<div class="flex items-center justify-between">
					<div>
						<p class="text-xs lg:text-sm text-text-secondary mb-1">
							Inactive Policies
						</p>
						<p class="text-xl lg:text-2xl font-bold text-red-600">
							{{ inactivePoliciesCount }}
						</p>
					</div>
					<div
						class="w-10 h-10 lg:w-12 lg:h-12 bg-red-50 rounded-lg flex items-center justify-center"
					>
						<svg
							class="w-5 h-5 lg:w-6 lg:h-6 text-red-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</div>
				</div>
			</div>
		</div>

		<!-- Policies Grid -->
		<div
			v-if="selectPoliciesData && selectPoliciesData.length > 0"
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
		>
			<div
				v-for="policy in selectPoliciesData"
				:key="policy.id"
				class="bg-white border border-border rounded-lg p-5 hover:shadow-md transition-all duration-300 group"
			>
				<!-- Header Section -->
				<div class="mb-4">
					<div class="flex items-start justify-between mb-3">
						<h2
							class="text-base lg:text-lg font-semibold text-text-primary line-clamp-2 flex-1 mr-2"
						>
							{{ policy.policyType }}
						</h2>
						<div class="flex-shrink-0">
							<span
								class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
								:class="{
									'bg-green-50 text-green-700':
										policy.policyStatus === 'ACTIVE',
									'bg-red-50 text-red-700':
										policy.policyStatus !== 'ACTIVE',
								}"
							>
								<span
									class="w-1.5 h-1.5 rounded-full mr-1.5"
									:class="{
										'bg-green-500':
											policy.policyStatus === 'ACTIVE',
										'bg-red-500':
											policy.policyStatus !== 'ACTIVE',
									}"
								></span>
								{{ policy.policyStatus }}
							</span>
						</div>
					</div>

					<!-- Policy Number -->
					<div
						class="flex items-center text-xs lg:text-sm text-text-secondary mb-2"
					>
						<svg
							class="w-3 h-3 lg:w-4 lg:h-4 mr-1.5 text-text-secondary/60"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
							/>
						</svg>
						<span class="font-medium text-text-primary"
							>Policy #{{ policy.policyNumber }}</span
						>
					</div>

					<!-- Risk Info -->
					<div
						v-if="policy.risks && policy.risks.length > 0"
						class="flex items-center text-xs lg:text-sm text-text-secondary"
					>
						<svg
							class="w-3 h-3 lg:w-4 lg:h-4 mr-1.5 text-text-secondary/60"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						<span class="font-medium text-text-primary">{{
							policy.risks[0].riskRef
						}}</span>
						<span class="mx-1">•</span>
						<span
							>{{ policy.quotationCurrency }}
							{{
								policy.risks[0].value
									? policy.risks[0].value.toLocaleString()
									: 'N/A'
							}}</span
						>
					</div>
				</div>

				<!-- Premium Section -->
				<div
					v-if="policy.quotationPremium"
					class="bg-primary/5 rounded-lg p-4 mb-4 border border-primary/10"
				>
					<div class="flex items-center justify-between mb-2">
						<span
							class="text-xs lg:text-sm font-medium text-text-secondary"
							>Total Premium</span
						>
						<span
							class="text-lg lg:text-2xl font-bold text-primary"
						>
							{{ policy.quotationCurrency }}
							{{
								policy.quotationPremium.totalPremium.toLocaleString()
							}}
						</span>
					</div>
					<div
						class="flex items-center justify-between text-xs text-text-secondary gap-2"
					>
						<span class="font-semibold"
							>Base: {{ policy.quotationCurrency }}
							{{
								policy.quotationPremium.basePremium.toLocaleString()
							}}</span
						>
						<span class="font-semibold"
							>Charges: {{ policy.quotationCurrency }}
							{{
								policy.quotationPremium.totalCharges
									? policy.quotationPremium.totalCharges.toLocaleString()
									: '0'
							}}</span
						>
					</div>
				</div>

				<!-- Client Details -->
				<div
					v-if="policy.upload && policy.upload.clientDetails"
					class="border-t border-border pt-3 mb-4"
				>
					<h3
						class="text-xs lg:text-sm font-semibold text-text-primary mb-2 flex items-center"
					>
						<svg
							class="w-3 h-3 lg:w-4 lg:h-4 mr-1.5 text-primary"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
							/>
						</svg>
						Client Information
					</h3>
					<div class="flex items-center space-x-3">
						<div class="flex-shrink-0">
							<div
								class="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center"
							>
								<span
									class="text-xs font-semibold text-primary"
								>
									{{
										getInitials(
											policy.upload.clientDetails.names,
										)
									}}
								</span>
							</div>
						</div>
						<div class="flex-1 min-w-0">
							<p
								class="text-xs lg:text-sm font-medium text-text-primary truncate"
							>
								{{
									getFullName(
										policy.upload.clientDetails.names,
									)
								}}
							</p>
							<p class="text-xs text-text-secondary truncate">
								{{ policy.upload.clientDetails.email }}
							</p>
						</div>
					</div>
				</div>

				<!-- Quotation Details -->
				<div
					v-if="policy.upload"
					class="bg-surface rounded-lg p-3 mb-4"
				>
					<div class="grid grid-cols-2 gap-2 text-xs">
						<div>
							<span class="text-text-secondary">Channel:</span>
							<p class="font-medium text-text-primary">
								{{ policy.quotationChannel }}
							</p>
						</div>
						<div>
							<span class="text-text-secondary">Currency:</span>
							<p class="font-medium text-text-primary">
								{{ policy.quotationCurrency }}
							</p>
						</div>
					</div>
				</div>

				<!-- Action Buttons -->
				<div class="space-y-2">
					<button
						@click="handleViewPolicy(policy)"
						class="w-full inline-flex items-center justify-center px-3 py-2 text-xs lg:text-sm font-semibold rounded-lg text-white bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
					>
						<svg
							class="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1.5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
							/>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
							/>
						</svg>
						View Policy
					</button>

					<!-- <button
              v-if="policy.policyStatus === 'ACTIVE'"
              @click="openReviewModal(policy)"
              class="w-full inline-flex items-center justify-center px-3 py-2 text-xs lg:text-sm font-semibold rounded-lg text-white bg-brand-cyan hover:bg-brand-cyan/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-cyan transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
          >
            <svg class="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
            </svg>
            Leave Review
          </button> -->
				</div>
			</div>
		</div>

		<!-- Empty State -->
		<div
			v-if="!selectPoliciesData || selectPoliciesData.length === 0"
			class="text-center py-12 bg-white border border-border rounded-lg"
		>
			<div
				class="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-surface mb-3"
			>
				<svg
					class="w-7 h-7 text-text-secondary"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
					/>
				</svg>
			</div>
			<h3
				class="text-base lg:text-lg font-semibold text-text-primary mb-1"
			>
				No policies found
			</h3>
			<p class="text-xs lg:text-sm text-text-secondary mb-4">
				You don't have any insurance policies yet. Get started by
				creating your first policy.
			</p>
			<button
				class="inline-flex items-center px-4 py-2 text-xs lg:text-sm font-semibold text-white bg-primary hover:bg-primary-light rounded-lg transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
			>
				<svg
					class="w-4 h-4 mr-1.5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 4v16m8-8H4"
					/>
				</svg>
				Create Policy
			</button>
		</div>

		<!-- Pagination -->
		<div
			v-if="selectPoliciesData && selectPoliciesData.length > 0"
			class="mt-6"
		>
			<Pagination @page-change="fetchClientPolicies" />
		</div>

		<!-- Review Modal -->
		<ReviewModal
			:isOpen="showReviewModal"
			@close="showReviewModal = false"
			@submit="handleReviewSubmit"
		/>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Pagination from '../templates/others/pagination.vue';
import ReviewModal from '../../insurance/motor/Motor_private/components/ReviewModal.vue';
import { FetchClientPolicies, FetchPolicyDetails } from '../api/portal';
import { getPageNumbers } from '../../../helpers/pagination';

export default {
	components: {
		Pagination,
		ReviewModal,
	},
	data() {
		return {
			showReviewModal: false,
			selectedPolicy: null,
		};
	},
	computed: {
		...mapGetters('quotations', ['selectPoliciesData']),
		...mapGetters('pagination', ['currentPage']),

		activePoliciesCount() {
			if (!this.selectPoliciesData) return 0;
			return this.selectPoliciesData.filter(
				(policy) => policy.policyStatus === 'ACTIVE',
			).length;
		},

		inactivePoliciesCount() {
			if (!this.selectPoliciesData) return 0;
			return this.selectPoliciesData.filter(
				(policy) => policy.policyStatus !== 'ACTIVE',
			).length;
		},
	},
	methods: {
		...mapActions('quotations', [
			'setClientPolicy',
			'setClientPoliciesData',
		]),
		...mapActions('pagination', [
			'setPagesArray',
			'setTotalPages',
			'setCurrentPage',
		]),

		getFullName(names) {
			if (!names) return 'N/A';
			return (
				`${names.firstName || ''} ${names.fName || ''} ${names.lName || ''}`.trim() ||
				'N/A'
			);
		},

		getInitials(names) {
			if (!names) return 'NA';
			const firstName = names.firstName || names.fName || '';
			const lastName = names.lastName || names.lName || '';
			return (
				`${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() ||
				'NA'
			);
		},

		async handleViewPolicy(policy) {
			try {
				const res = await FetchPolicyDetails({
					policySystemRef: policy.policySystemRef,
				});
				if (res.status) {
					this.setClientPolicy(res.data);
					this.$router.push(`products/${policy.id}`);
				}
			} catch (err) {
				const errorMessage =
					err.response &&
					err.response.data &&
					err.response.data.errors
						? err.response.data.errors
						: 'An error occurred fetching policy details';
				this.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: `${errorMessage}. Please try again`,
					duration: 3000,
				});
			}
		},

		async fetchClientPolicies() {
			try {
				const fetchData = {
					querryOptions: {
						page: this.currentPage,
					},
				};
				const res = await FetchClientPolicies(fetchData);
				if (res.status) {
					this.setClientPoliciesData(res.data[0].items);
					const pages = getPageNumbers(res.data[0].pageCount, 20);
					this.setPagesArray(pages);
					this.setTotalPages(res.data[0].totalCount);
					this.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: 'Policies fetched successfully',
						duration: 3000,
					});
				}
			} catch (error) {
				console.error('Error fetching policies:', error);
				this.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: 'Failed to fetch policies',
					duration: 3000,
				});
			}
		},

		openReviewModal(policy) {
			this.selectedPolicy = policy;
			this.showReviewModal = true;
		},

		handleReviewSubmit({ rating, message }) {
			console.log('Review submitted for policy:', {
				policy: this.selectedPolicy,
				rating,
				message,
			});
			this.$emit('notifications:show', {
				type: 'success',
				title: 'Review Submitted',
				message: 'Thank you for your feedback!',
				duration: 3000,
			});
			this.showReviewModal = false;
			this.selectedPolicy = null;
		},
	},

	async mounted() {
		this.setCurrentPage(1);
		await this.fetchClientPolicies();
		console.log('policiesData', this.selectPoliciesData);
	},
};
</script>

<style scoped>
.line-clamp-2 {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	line-clamp: 2;
	overflow: hidden;
}

/* Focus styles for accessibility */
button:focus-visible {
	outline: 2px solid theme('colors.primary.DEFAULT');
	outline-offset: 2px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.text-3xl {
		font-size: 1.875rem;
		line-height: 2.25rem;
	}
}
</style>
